import React, { useEffect, useState } from "react";
import { useQuestionnaireResponses } from "@/hooks/useQuestionnaireResponses";
import { toLocalDate } from "@/utils/datetime";
import {
  DealsQuestionnaireData,
  QuestionResponse,
  QuestionValueType,
  Responses,
  Score
} from "@/pages/deals/types";
import {
  useDeals,
  useDeleteQuestionMutation,
  useUpdateDealQuestionnaireMutation
} from "@/pages/deals/hooks";
import toast from "react-hot-toast";
import Text from "@/components/ui/text";
import { Dollar, InfoCircle } from "@dreamhub/designsystem/icons";
import { useTranslation } from "react-i18next";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import FormNew from "@/components/controls/Form/FormNew";
import { FormSectionProps } from "@/components/controls/Form/FormSection";
import { FormFieldNewType } from "@/components/controls/Form/types";
import ConfirmationModal from "@/components/layouts/ConfirmationModal";
import { TFunction } from "i18next";
import SuccessCriteria from "@/components/controls/SuccessCriteria/SuccessCriteria";
import { useQueryClient } from "@tanstack/react-query";
import IconContainer from "@/components/ui/icon-container";
import { find, isNull } from "lodash";

interface Props {
  dealId: string;
  questionnaire: DealsQuestionnaireData;
  handleUpdateQuestionnaireScore: (newValue: Score | null) => void;
  responses?: Responses;
}

export interface DeleteCriterionModalState {
  isOpen: boolean;
  questionId: string | null;
}

export const renderSectionTitle = (
  t: TFunction<"translation", undefined>,
  questionnaireName: string,
  sectionName: string
) => (
  <div className="flex gap-2 items-center">
    <Text variant="label">
      {t(`deals.evaluationQuestionnaire.${sectionName}.title`, {
        questionnaireName
      })}
    </Text>
    <Tooltip>
      <TooltipTrigger>
        <IconContainer color="tertiary">
          <InfoCircle />
        </IconContainer>
      </TooltipTrigger>
      <TooltipContent maxWidth={18}>
        {t(`deals.evaluationQuestionnaire.${sectionName}.tooltip`)}
      </TooltipContent>
    </Tooltip>
  </div>
);

const POCQuestionnaire: React.FC<Props> = ({
  dealId,
  questionnaire,
  handleUpdateQuestionnaireScore,
  responses
}) => {
  const { t } = useTranslation();

  //TODO: disable form submission with isFormValid (on "send for stakeholders approval")
  const [_isFormValid, setIsFormValid] = useState(false);
  const { localResponses, updateResponse, resetResponses } =
    useQuestionnaireResponses(responses);
  const [startDate, setStartDate] = useState("");

  const [questions, setQuestions] = useState(questionnaire.questions);
  const [deleteCriterionModalState, setDeleteCriterionModalState] =
    useState<DeleteCriterionModalState>({
      isOpen: false,
      questionId: null
    });

  const updateQuestionnaire = useUpdateDealQuestionnaireMutation();
  const deleteQuestion = useDeleteQuestionMutation();

  const queryClient = useQueryClient();

  const {
    entities: [dealData]
  } = useDeals([dealId || ""]);

  if (!dealData) return;

  useEffect(() => {
    setQuestions(questionnaire.questions);
  }, [questionnaire.questions]);

  const setIsDeleteCriterionModalOpen: React.Dispatch<
    React.SetStateAction<boolean>
  > = (value) => {
    setDeleteCriterionModalState((prevState) => ({
      ...prevState,
      isOpen: typeof value === "function" ? value(prevState.isOpen) : value
    }));
  };

  const getQuestionByName = (name: string) => {
    return find(questions, (question) => question.fieldName === name);
  };

  const getDefaultValue = (questionName: string) => {
    const question = getQuestionByName(questionName);
    return question ? localResponses[question.id]?.response?.value : "";
  };

  const isValidEndDate = (value: string) =>
    toLocalDate(value) > toLocalDate(startDate);

  const handleValueChange = (value: QuestionValueType, questionId: string) => {
    const existingResponse =
      localResponses[questionId] || ({} as QuestionResponse | null);

    const newResponse = {
      ...existingResponse,
      response: { value }
    };

    updateResponse(questionId, newResponse);
    updateQuestionnaire.mutate(
      {
        pathParams: { dealId, questionnaireId: questionnaire.id },
        data: {
          questions: [
            {
              questionId,
              ...newResponse
            }
          ]
        }
      },
      {
        onSuccess: (data) => {
          handleUpdateQuestionnaireScore(data.score);
        }
      }
    );
  };

  const handleDeleteCriteria = () => {
    if (isNull(deleteCriterionModalState.questionId)) return;
    deleteQuestion.mutate(
      {
        pathParams: {
          questionId: deleteCriterionModalState.questionId
        },
        queryParams: {}
      },
      {
        onSuccess: () => {
          setQuestions(
            (questions || []).filter(
              (question) => question.id !== deleteCriterionModalState.questionId
            )
          );
          setDeleteCriterionModalState({
            isOpen: false,
            questionId: null
          });
          queryClient.invalidateQueries({
            queryKey: ["settings/questionnaireSettings"]
          });
          toast.success(
            t("deals.evaluationQuestionnaire.successCriteria.questionDeleted"),
            {
              duration: 5000,
              icon: null
            }
          );
        }
      }
    );
  };

  const getDateFields = (): FormFieldNewType[] => {
    return [
      {
        name: "startDate",
        label: t("deals.evaluationQuestionnaire.startDate.name"),
        type: "date",
        required: getQuestionByName("startDate")?.isMandatory || false,
        layout: "half",
        defaultValue: getDefaultValue("startDate") as string,
        fieldActionProps: {
          placeholder: t(
            "deals.evaluationQuestionnaire.startDate.placeholder",
            {
              questionnaireName: questionnaire?.name
            }
          ),
          onChange: (value: string | string[]) => {
            setStartDate(value as string);
            handleValueChange(
              value as string,
              getQuestionByName("startDate")?.id || ""
            );
          }
        }
      },
      {
        name: "endDate",
        label: t("deals.evaluationQuestionnaire.endDate.name"),
        type: "date",
        required: getQuestionByName("endDate")?.isMandatory || false,
        layout: "half",
        defaultValue: getDefaultValue("endDate") as string,
        validation: {
          customRules: [
            {
              validate: (value: string) => {
                if (startDate && value) {
                  return isValidEndDate(value);
                }
                return true;
              },
              message: t("deals.evaluationQuestionnaire.endDate.error")
            }
          ]
        },
        fieldActionProps: {
          placeholder: t("deals.evaluationQuestionnaire.endDate.placeholder", {
            questionnaireName: questionnaire?.name
          }),
          onChange: (value: string | string[]) => {
            const isValid =
              startDate && value ? isValidEndDate(value as string) : true;
            if (isValid) {
              handleValueChange(
                value as string,
                getQuestionByName("endDate")?.id || ""
              );
            }
          }
        }
      }
    ];
  };

  const getRightSideFields = (): FormFieldNewType[] => {
    return [
      {
        name: "scope",
        label: t("deals.evaluationQuestionnaire.scope.name"),
        type: "textEditor",
        required: getQuestionByName("scope")?.isMandatory || false,
        defaultValue: getDefaultValue("scope") as string,
        onBlur: (value: string) =>
          handleValueChange(
            value as string,
            getQuestionByName("scope")?.id || ""
          ),
        fieldActionProps: {
          placeholder: t("deals.evaluationQuestionnaire.scope.placeholder", {
            questionnaireName: questionnaire?.name
          }),
          minRows: 4
        }
      }
    ];
  };

  const getFormSections = (): FormSectionProps[] => {
    return [
      {
        id: "date-fields",
        fields: getDateFields(),
        halfFieldClassName: "mt-1 ml-2"
      },
      {
        id: "cost-field",
        fields: [
          {
            name: "cost",
            label: t("deals.evaluationQuestionnaire.cost.name"),
            type: "number",
            required: getQuestionByName("cost")?.isMandatory || false,
            defaultValue: getDefaultValue("cost") as string,
            onBlur: (value: string) =>
              handleValueChange(
                value as string,
                getQuestionByName("cost")?.id || ""
              ),
            fieldActionProps: {
              placeholder: t("deals.evaluationQuestionnaire.cost.placeholder", {
                questionnaireName: questionnaire?.name
              }),
              trailingIcon: <Dollar />
            }
          }
        ]
      },
      {
        id: "scope-field",
        fields: getRightSideFields()
      }
    ];
  };

  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-6 flex-1 pt-8 px-6">
        {renderSectionTitle(t, questionnaire?.name, "evaluation")}
        <div className="flex flex-col gap-8 px-2">
          <FormNew
            key={`questionnaire-form-${getDefaultValue("startDate")}-${getDefaultValue("endDate")}-${getDefaultValue("cost")}-${getDefaultValue("scope")}`}
            sections={getFormSections()}
            isFormValid={(isValid) => setIsFormValid(isValid)}
          />
        </div>
      </div>
      {questionnaire?.isSuccessCriteriaEnabled && (
        <>
          <div className="h-px w-full bg-border-border1 mt-8 mb-6" />
          <SuccessCriteria
            dealId={dealId}
            questionnaireId={questionnaire.id}
            questionnaire={questionnaire}
            questions={questions}
            setQuestions={setQuestions}
            localResponses={localResponses}
            updateResponse={updateResponse}
            resetResponses={resetResponses}
            setDeleteCriterionModalState={setDeleteCriterionModalState}
            handleUpdateQuestionnaireScore={handleUpdateQuestionnaireScore}
            handleValueChange={handleValueChange}
          />
        </>
      )}
      <ConfirmationModal
        isOpen={deleteCriterionModalState.isOpen}
        setIsOpen={setIsDeleteCriterionModalOpen}
        title={t(
          "deals.evaluationQuestionnaire.successCriteria.deletionModal.title"
        )}
        confirmationText={t(
          "deals.evaluationQuestionnaire.successCriteria.deletionModal.confirmation"
        )}
        handleSubmit={handleDeleteCriteria}
      />
    </div>
  );
};

export default POCQuestionnaire;
